// Live Holdings page. GET /api/v1/portfolio/holdings[?client_id=].
// Per-(account, asset) rows from server; frontend does the aggregate +
// filter math so the UI choices stay local.

/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { AssetMark, ExportCsvLink, OwnershipPill, PageHeader, SortTable, Stat, VenuePill, venueShortLabel } from '../../components/shell';
import { fmtPct, fmtQty, fmtUSD } from '../../lib/format';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { usePolling } from '../../lib/usePolling';

const HOLDINGS_POLL_MS = 60_000;

const ASSET_NAMES: Record<string, string> = {
  BTC: 'Bitcoin', ETH: 'Ether', SOL: 'Solana', ARB: 'Arbitrum',
  MATIC: 'Polygon', USDC: 'USD Coin', USDT: 'Tether', USD: 'US Dollar',
};

const VENUE_LABELS: Record<string, string> = {
  coinbase_exchange: 'Coinbase Exchange',
  bybit: 'Bybit',
  kraken: 'Kraken',
  onchain: 'On-chain',  // overridden per-row by chain when present
};

// venue=onchain rows render the chain-specific label (Ethereum / BNB
// Smart Chain). Other venues use VENUE_LABELS.
const CHAIN_LABELS: Record<string, string> = {
  ethereum: 'Ethereum',
  bsc: 'BNB Smart Chain',
};
function venueLabelFor(venue: string, chain?: string): string {
  if (venue === 'onchain' && chain && CHAIN_LABELS[chain]) return CHAIN_LABELS[chain];
  return VENUE_LABELS[venue] || venue;
}

interface HoldingsAccount {
  id: string;
  label: string;
  venue: 'coinbase_exchange' | 'bybit' | 'onchain' | 'kraken';
  chain?: string;
  ownership: 'client_owned' | 'company_owned';
  opened_at: string;
  value: number;
}

interface PositionRow {
  account_id: string;
  asset: string;
  quantity: number;
  cost_basis_usd: number;
  avg_cost_usd: number;
  market_price_usd: number;
  market_value_usd: number;
  unrealized_pnl_usd: number;
}

interface HoldingsPayload {
  as_of: string | null;
  accounts: HoldingsAccount[];
  positions: PositionRow[];
}

export default function Holdings() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { user } = useAuth();
  const clientId = params.get('client_id');

  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  // Phase 9.b — Fund-position pointer. SMA Holdings page is empty for
  // Fund-only investors; this banner points them to /fund whenever they
  // hold any unit position.
  const [fundPositionsCount, setFundPositionsCount] = useState(0);
  const [venueFilter, setVenueFilter] = useState<string>('all');
  const [ownerFilter, setOwnerFilter] = useState<string>('all');
  // Hide positions with no USD value by default. Active wallets accumulate
  // long-tail tokens (airdrops, BSC memes) that have no CoinGecko coverage —
  // showing all of them buries the real positions. Toggleable for audit.
  const [showUnpriced, setShowUnpriced] = useState<boolean>(false);

  useEffect(() => {
    if (user && user.role !== 'client' && !clientId) {
      navigate('/fund', { replace: true });
    }
  }, [user, clientId, navigate]);

  const url = '/portfolio/holdings' + (clientId ? `?client_id=${clientId}` : '');
  const refreshUrl = '/portfolio/refresh' + (clientId ? `?client_id=${clientId}` : '');
  const pollingEnabled = !!user && (user.role === 'client' || !!clientId);
  const { data, error, refresh } = usePolling<HoldingsPayload>(
    (signal) => api<HoldingsPayload>(url, { signal }),
    { intervalMs: HOLDINGS_POLL_MS, deps: [url], enabled: pollingEnabled },
  );

  // Phase 9.b — fetch fund positions for the cross-link banner.
  useEffect(() => {
    if (!pollingEnabled) return;
    const fundUrl = '/portfolio/fund' + (clientId ? `?client_id=${clientId}` : '');
    api<{ positions: any[] }>(fundUrl)
      .then(r => setFundPositionsCount(r?.positions?.length || 0))
      .catch(() => setFundPositionsCount(0));
  }, [pollingEnabled, clientId]);

  // Pull fresh data from the venue on page open (see Dashboard for rationale).
  useEffect(() => {
    if (!pollingEnabled) return;
    let cancelled = false;
    api(refreshUrl, { method: 'POST' })
      .catch(() => { /* fall through to cached snapshot */ })
      .finally(() => { if (!cancelled) refresh(); });
    return () => { cancelled = true; };
  }, [refreshUrl, pollingEnabled, refresh]);

  useEffect(() => {
    if (!error) { setErrorMsg(null); return; }
    const status = (error as { status?: number })?.status;
    if (status === 404 && user?.role !== 'client') {
      navigate('/fund', { replace: true });
      return;
    }
    setErrorMsg(status === 409 ? 'Your portfolio is being set up. Transactions and reports will appear here once trading begins.' : 'Failed to load holdings.');
  }, [error, user, navigate]);

  // Venue chips are DERIVED from the accounts present, not hardcoded. The
  // static list carried BYBIT — a venue removed from the product at item 19 —
  // and on prod (18 accounts, all on-chain) COINBASE and KRAKEN were empty too,
  // so three of five chips could only ever return nothing (measured c319).
  // Deriving fixes the class rather than the one chip that was reported, and a
  // venue added or retired later needs no edit here.
  const venueOptions = useMemo<string[]>(() => {
    if (!data) return [];
    const present = new Set<string>(data.accounts.map(a => a.venue).filter(Boolean));
    return [...present].sort();
  }, [data]);

  // If the selected venue vanishes (data refresh, account closed), fall back to
  // ALL — otherwise the user is stuck on an empty list with no chip to undo it.
  useEffect(() => {
    if (venueFilter !== 'all' && venueOptions.length && !venueOptions.includes(venueFilter)) {
      setVenueFilter('all');
    }
  }, [venueOptions, venueFilter]);

  const filteredPositions = useMemo(() => {
    if (!data) return [];
    const accountById = new Map(data.accounts.map(a => [a.id, a]));
    return data.positions.filter(p => {
      const acct = accountById.get(p.account_id);
      if (!acct) return false;
      if (venueFilter !== 'all' && acct.venue !== venueFilter) return false;
      if (ownerFilter !== 'all' && acct.ownership !== ownerFilter) return false;
      if (!showUnpriced && p.market_value_usd <= 0) return false;
      return true;
    });
  }, [data, venueFilter, ownerFilter, showUnpriced]);

  // Count what's currently hidden so the disclosure line below the table
  // ("N positions held with no USD price — Show all") shows the right number.
  const unpricedCount = useMemo(() => {
    if (!data) return 0;
    const accountById = new Map(data.accounts.map(a => [a.id, a]));
    return data.positions.filter(p => {
      const acct = accountById.get(p.account_id);
      if (!acct) return false;
      if (venueFilter !== 'all' && acct.venue !== venueFilter) return false;
      if (ownerFilter !== 'all' && acct.ownership !== ownerFilter) return false;
      return p.market_value_usd <= 0;
    }).length;
  }, [data, venueFilter, ownerFilter]);

  const aggregated = useMemo(() => {
    const map = new Map<string, any>();
    filteredPositions.forEach(p => {
      const prev = map.get(p.asset) || {
        asset: p.asset, name: ASSET_NAMES[p.asset] || p.asset,
        qty: 0, cost: 0, value: 0, price: p.market_price_usd,
      };
      prev.qty += p.quantity;
      prev.cost += p.cost_basis_usd;
      prev.value += p.market_value_usd;
      prev.price = p.market_price_usd;
      map.set(p.asset, prev);
    });
    return [...map.values()].map((r: any) => ({
      ...r,
      avgCost: r.qty > 0 ? r.cost / r.qty : 0,
      pl: r.value - r.cost,
      plPct: r.cost !== 0 ? ((r.value - r.cost) / r.cost) * 100 : 0,
    }));
  }, [filteredPositions]);

  if (errorMsg) {
    return (
      <div className="view container">
        <p style={{ fontFamily: 'var(--serif)', fontStyle: 'italic', color: 'var(--accent)' }}>{errorMsg}</p>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="view container">
        <p style={{ fontFamily: 'var(--mono)', fontSize: 11, letterSpacing: '0.16em',
                    textTransform: 'uppercase', color: 'var(--ink-3)' }}>
          Loading…
        </p>
      </div>
    );
  }

  const totalValue = aggregated.reduce((s: number, r: any) => s + r.value, 0);
  const totalCost = aggregated.reduce((s: number, r: any) => s + r.cost, 0);
  const totalPl = totalValue - totalCost;
  const asOfText = data.as_of ? new Date(data.as_of).toISOString().slice(0, 16).replace('T', ' ') + ' UTC' : '—';

  return (
    <div className="view container clean-screen">
      {fundPositionsCount > 0 && (
        <div className="label-s" style={{
          marginBottom: 24, padding: '12px 16px',
          background: 'var(--paper-2)', border: 'var(--hairline)',
          borderColor: 'var(--rule)', display: 'flex',
          justifyContent: 'space-between', alignItems: 'center',
        }}>
          <span>YOU HOLD UNITS IN {fundPositionsCount === 1 ? '1 FUND' : `${fundPositionsCount} FUNDS`} · NOT SHOWN BELOW (SMA HOLDINGS ONLY)</span>
          <a href={clientId ? `/fund?client_id=${clientId}` : '/fund'} style={{ color: 'var(--ink)' }}>
            VIEW FUND POSITION →
          </a>
        </div>
      )}
      <PageHeader
        kicker="HOLDINGS"
        title={<><em>Current</em> positions.</>}
        meta={
          <>
            <span className="label-s">AS OF {asOfText}</span>
            <span className="label-s">{aggregated.length} ASSETS · {data.accounts.length} ACCOUNTS</span>
          </>
        }
      />

      <div className="agg-strip" style={{ ['--cols' as any]: 3 } as any}>
        <div className="agg-cell">
          <Stat label="TOTAL VALUE" value={fmtUSD(totalValue)} sub={`${aggregated.length} POSITIONS`} />
        </div>
        <div className="agg-cell">
          <Stat label="COST BASIS" value={fmtUSD(totalCost)} sub="ALL ACCOUNTS" />
        </div>
        <div className="agg-cell">
          <Stat
            label="UNREALIZED P&L"
            value={<span className={totalPl >= 0 ? 'pos' : 'neg'}>{fmtUSD(totalPl)}</span>}
            sub={
              totalCost !== 0
                ? <><span className={totalPl >= 0 ? 'pos' : 'neg'}>{fmtPct((totalPl / totalCost) * 100)}</span> &nbsp;UNREALIZED</>
                : 'NO COST BASIS YET'
            }
          />
        </div>
      </div>

      <div className="filter-bar" style={{ marginTop: 56 }}>
        <span className="label" style={{ marginRight: 12 }}>VENUE</span>
        <div className="filter-group">
          {[['all', 'ALL'] as const,
            ...venueOptions.map(v => [v, venueShortLabel(venueLabelFor(v))] as const),
          ].map(([v, label]) => (
            <button key={v} className="filter-chip"
                    data-active={venueFilter === v ? '1' : '0'}
                    onClick={() => setVenueFilter(v)}>
              {label}
            </button>
          ))}
        </div>
        <span style={{ width: 24 }} />
        <span className="label" style={{ marginRight: 12 }}>OWNERSHIP</span>
        <div className="filter-group">
          {[
            ['all', 'ALL'], ['client_owned', 'CLIENT'], ['company_owned', 'COMPANY'],
          ].map(([v, label]) => (
            <button key={v} className="filter-chip"
                    data-active={ownerFilter === v ? '1' : '0'}
                    onClick={() => setOwnerFilter(v!)}>
              {label}
            </button>
          ))}
        </div>
        <span style={{ marginLeft: 'auto' }} />
        <ExportCsvLink endpoint="holdings" clientId={clientId} />
      </div>

      <section className="clean-panel" style={{ marginTop: 32 }}>
        <SortTable
          defaultSort="value"
          rows={aggregated}
          emptyMessage="No positions match the selected filters."
          columns={[
            {
              key: 'asset', label: 'ASSET',
              render: (r: any) => (
                <div className="asset">
                  <AssetMark asset={r.asset} />
                  <div>
                    <div className="asset-name">{r.name}</div>
                    <div className="asset-ticker">{r.asset}</div>
                  </div>
                </div>
              ),
            },
            {
              key: 'qty', label: 'QUANTITY', align: 'right',
              render: (r: any) => <span className="num">{fmtQty(r.qty, ['USDC', 'USDT', 'USD'].includes(r.asset) ? 2 : 4)}</span>,
            },
            {
              key: 'avgCost', label: 'AVG COST', align: 'right',
              render: (r: any) => <span className="num">{fmtUSD(r.avgCost, r.avgCost < 10 ? 4 : 2)}</span>,
            },
            {
              key: 'price', label: 'MARKET PRICE', align: 'right',
              render: (r: any) => <span className="num">{fmtUSD(r.price, r.price < 10 ? 4 : 2)}</span>,
            },
            {
              key: 'value', label: 'MARKET VALUE', align: 'right',
              render: (r: any) => <span className="num">{fmtUSD(r.value)}</span>,
            },
            {
              key: 'pl', label: 'UNREALIZED P&L', align: 'right',
              render: (r: any) => (
                <span className={'num ' + (r.pl >= 0 ? 'pos' : 'neg')}>
                  {r.pl >= 0 ? '+' : '−'}{fmtUSD(Math.abs(r.pl))}
                  <div className="label-s" style={{ marginTop: 4, color: r.pl >= 0 ? 'var(--accent)' : 'var(--oxblood)' }}>
                    {fmtPct(r.plPct)}
                  </div>
                </span>
              ),
            },
          ]}
        />
      </section>

      <section className="clean-hide" style={{ marginTop: 96 }}>
        <div className="kicker-row">
          <span className="kicker">BREAKDOWN BY ACCOUNT</span>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 56 }}>
          {data.accounts.map(acct => {
            const acctHoldings = filteredPositions.filter(p => p.account_id === acct.id);
            if (!acctHoldings.length) return null;
            const acctValue = acctHoldings.reduce((s, p) => s + p.market_value_usd, 0);
            return (
              <div key={acct.id}>
                <div style={{
                  display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: 16,
                  paddingBottom: 12, borderBottom: 'var(--hairline)', marginBottom: 16,
                }}>
                  <span className="display-m" style={{ fontSize: 26 }}>{acct.label || '—'}</span>
                  <VenuePill venue={venueLabelFor(acct.venue, acct.chain) as any} />
                  <OwnershipPill ownership={acct.ownership === 'client_owned' ? 'client-owned' as any : 'company-held' as any} />
                  <span style={{ marginLeft: 'auto' }} className="num">{fmtUSD(acctValue)}</span>
                </div>
                <table className="tbl" style={{ borderTop: 0 }}>
                  <thead>
                    <tr>
                      <th>ASSET</th>
                      <th data-align="right">QUANTITY</th>
                      <th data-align="right">AVG COST</th>
                      <th data-align="right">PRICE</th>
                      <th data-align="right">VALUE</th>
                      <th data-align="right">% OF ACCOUNT</th>
                    </tr>
                  </thead>
                  <tbody>
                    {acctHoldings.map(p => (
                      <tr key={p.asset + acct.id}>
                        <td>
                          <div className="asset">
                            <AssetMark asset={p.asset} />
                            <div>
                              <div className="asset-name">{ASSET_NAMES[p.asset] || p.asset}</div>
                              <div className="asset-ticker">{p.asset}</div>
                            </div>
                          </div>
                        </td>
                        <td data-align="right" className="num">{fmtQty(p.quantity, ['USDC', 'USDT', 'USD'].includes(p.asset) ? 2 : 4)}</td>
                        <td data-align="right" className="num">{fmtUSD(p.avg_cost_usd, p.avg_cost_usd < 10 ? 4 : 2)}</td>
                        <td data-align="right" className="num">{fmtUSD(p.market_price_usd, p.market_price_usd < 10 ? 4 : 2)}</td>
                        <td data-align="right" className="num">{fmtUSD(p.market_value_usd)}</td>
                        <td data-align="right" className="num" style={{ color: 'var(--ink-3)' }}>
                          {acctValue > 0 ? ((p.market_value_usd / acctValue) * 100).toFixed(2) : '0.00'}%
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          })}
        </div>

      </section>

      {/* Phase 9.b LOW 13 — unpriced disclosure rendered OUTSIDE the    */}
      {/* clean-hide section so transparency holds in clean mode too.    */}
      {unpricedCount > 0 && (
        <section style={{ marginTop: 24 }}>
          <div style={{
            paddingTop: 16, borderTop: 'var(--hairline)',
            display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
            gap: 24, flexWrap: 'wrap',
          }}>
            <span style={{
              fontFamily: 'var(--mono)', fontSize: 11, letterSpacing: '0.16em',
              textTransform: 'uppercase', color: 'var(--ink-3)',
            }}>
              {showUnpriced
                ? `${unpricedCount} position${unpricedCount === 1 ? '' : 's'} below shown without USD price`
                : `+ ${unpricedCount} position${unpricedCount === 1 ? '' : 's'} held with no USD price`}
            </span>
            <button className="btn-link" onClick={() => setShowUnpriced(v => !v)}>
              {showUnpriced ? 'Hide unpriced ←' : 'Show all →'}
            </button>
          </div>
        </section>
      )}

      <section className="clean-hide" style={{ marginTop: 64 }}>
        <div style={{ maxWidth: '76ch' }}>
          <div className="label" style={{ marginBottom: 12 }}>RECONCILIATION</div>
          <p className="serif-body" style={{ margin: 0, fontSize: 16 }}>
            Holdings and balances are reconciled daily against venue records. As-of times reflect the most recent reconciled snapshot.
          </p>
        </div>
      </section>
    </div>
  );
}
